// Reporting/index.js — orchestrator
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../../services/api';
import { Container, Typography, Box, Paper, Tabs, Tab } from '@mui/material';
import { Science as ScienceIcon } from '@mui/icons-material';
import { ArrowUpward as ArrowUpwardIcon, ArrowDownward as ArrowDownwardIcon, UnfoldMore as UnfoldMoreIcon } from '@mui/icons-material';
import DashboardLayout from '../../components/DashboardLayout';
import DateRangeFilter, { getPresetDates } from '../../components/DateRangeFilter';
import { ReportingContext } from './ReportingContext';
import OfferStatsTab from './tabs/OfferStatsTab';
import AffiliateStatsTab from './tabs/AffiliateStatsTab';
import BlogStatsTab from './tabs/BlogStatsTab';
import SnakeStatsTab from './tabs/SnakeStatsTab';
import CategoryStatsTab from './tabs/CategoryStatsTab';
import PlatformSettingsTab from './tabs/PlatformSettingsTab';
import RevShareTestTab from './tabs/RevShareTestTab';

const showRevTestTab = process.env.REACT_APP_SHOW_REV_TEST_TAB === 'true';


const Reporting = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [adsenseSuccess, setAdsenseSuccess] = useState(false);
  const [adsensePingResult, setAdsensePingResult] = useState(null);
  const [adsensePingLoading, setAdsensePingLoading] = useState(false);
  const [adsenseGlobalConnected, setAdsenseGlobalConnected] = useState(false);
  const [adsenseGlobalEmail, setAdsenseGlobalEmail] = useState(null);
  const [checkingAdsenseStatus, setCheckingAdsenseStatus] = useState(true);

  // Check initial AdSense global status
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await api.get('/adsense/status');
        setAdsenseGlobalConnected(res.data.connected);
        if (res.data.email) {
          setAdsenseGlobalEmail(res.data.email);
        }
      } catch (err) {
        console.error('Failed to check AdSense status:', err);
      } finally {
        setCheckingAdsenseStatus(false);
      }
    };
    checkStatus();
  }, []);

  // Handle AdSense callback redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('adsense') === 'success') {
      setAdsenseSuccess(true);
      setActiveTab(5); // Switch to Platform settings tab
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Mock data for other tabs (not Offer Stats)


  const affiliateStatsData = {
    totalAffiliates: 89,
    activeAffiliates: 67,
    topPerformers: [
      { name: 'john@email.com', commissions: '$2,450', conversions: 45 },
      { name: 'sarah@email.com', commissions: '$1,890', conversions: 38 },
      { name: 'mike@email.com', commissions: '$1,650', conversions: 32 },
      { name: 'lisa@email.com', commissions: '$1,320', conversions: 28 },
      { name: 'alex@email.com', commissions: '$980', conversions: 22 }
    ]
  };

  // Blog Stats - Real state
  const [blogStats, setBlogStats] = useState(null);
  const [blogStatsLoading, setBlogStatsLoading] = useState(true);
  const [blogStatsError, setBlogStatsError] = useState('');
  const [expandedBlogs, setExpandedBlogs] = useState({});
  const [expandedAffiliates, setExpandedAffiliates] = useState({});
  const [expandedSnakeBlogs, setExpandedSnakeBlogs] = useState({});
  const [expandedCCRows, setExpandedCCRows] = useState({});
  const [blogPreset, setBlogPreset] = useState('thisMonth');
  const [blogDateDates, setBlogDateDates] = useState(getPresetDates('thisMonth'));
  const [blogCategories, setBlogCategories] = useState([]);
  const [blogSelectedCategory, setBlogSelectedCategory] = useState('');
  const [blogSelectedSubcategory, setBlogSelectedSubcategory] = useState('');
  const [affiliates, setAffiliates] = useState([]);
  const [selectedAffiliateId, setSelectedAffiliateId] = useState('');
  const [viewAllAffiliates, setViewAllAffiliates] = useState(true);

  // dateRanges replaced by DateRangeFilter component

  const pieChartData = [
    { label: 'Active', value: 65, color: '#4caf50' },
    { label: 'Expired', value: 20, color: '#f44336' },
    { label: 'Draft', value: 10, color: '#ff9800' },
    { label: 'Pending', value: 5, color: '#9e9e9e' }
  ];

  const categoryData = [
    { label: 'Technology', value: 35, color: '#3B82F6' },
    { label: 'Fashion', value: 25, color: '#eabbc3' },
    { label: 'Health', value: 20, color: '#4caf50' },
    { label: 'Travel', value: 12, color: '#ff9800' },
    { label: 'Food', value: 8, color: '#9c27b0' }
  ];

  // State for Offer Stats
  const [offerStatsData, setOfferStatsData] = useState([]);
  const [offerStatsLoading, setOfferStatsLoading] = useState(true);
  const [offerStatsSummary, setOfferStatsSummary] = useState({
    totalClicks: 0,
    totalConversions: 0,
    totalRevenue: 0,
    averageCPA: 0
  });
  const [filterOptions, setFilterOptions] = useState({
    networks: [],
    brands: [],
    offers: []
  });

  // Filter states
  const [offerPreset, setOfferPreset] = useState('thisMonth');
  const [filters, setFilters] = useState({
    ...getPresetDates('thisMonth'),
    networkCompany: '',
    brandName: '',
    offerId: ''
  });

  // ── RevShare Test state ──────────────────────────────────────────────────
  const [revShareRunning, setRevShareRunning] = useState(false);
  const [revShareResult, setRevShareResult] = useState(null);
  const [revShareError, setRevShareError] = useState(null);
  const [revSharePayout, setRevSharePayout] = useState('25.00');

  const [revShareCleaning, setRevShareCleaning] = useState(false);

  const runRevShareTest = async () => {
    setRevShareRunning(true);
    setRevShareResult(null);
    setRevShareError(null);
    try {
      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
      const res = await axios.post(
        `${apiBase}/affiliate/test-revshare`,
        { payoutAmount: parseFloat(revSharePayout) || 25 },
        { withCredentials: true }
      );
      if (res.data.success) {
        setRevShareResult(res.data);
      } else {
        setRevShareError(res.data.error || 'Simulation failed');
      }
    } catch (err) {
      setRevShareError(err.response?.data?.error || err.message);
    } finally {
      setRevShareRunning(false);
    }
  };

  const cleanupRevShareTest = async (testRunId) => {
    setRevShareCleaning(true);
    try {
      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
      await axios.delete(`${apiBase}/affiliate/test-revshare/cleanup/${testRunId}`, { withCredentials: true });
      setRevShareResult(null);
    } catch (err) {
      setRevShareError(err.response?.data?.error || err.message);
    } finally {
      setRevShareCleaning(false);
    }
  };

  const clearAllReportingData = async () => {
    if (!window.confirm('Are you sure you want to PERMANENTLY clear ALL sale, click, and view data? This cannot be undone.')) {
      return;
    }
    
    setRevShareCleaning(true);
    try {
      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
      await axios.delete(`${apiBase}/affiliate/all-reporting-data`, { withCredentials: true });
      setRevShareResult(null);
      alert('All reporting data has been cleared successfully.');
      // Refresh the page or data
      window.location.reload();
    } catch (err) {
      setRevShareError(err.response?.data?.error || err.message);
    } finally {
      setRevShareCleaning(false);
    }
  };

  const runSimpleVerificationTest = async () => {
    setRevShareRunning(true);
    setRevShareError(null);
    try {
      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
      const res = await axios.post(`${apiBase}/affiliate/simple-test-revshare`, {}, { withCredentials: true });
      if (res.data.success) {
        alert(`Simple Verification Test Complete!\n\nExpected Results:\n- Affiliate Alpha: ${res.data.expectedResults.affiliateAlpha}\n- Affiliate Beta: ${res.data.expectedResults.affiliateBeta}\n- Platform: ${res.data.expectedResults.platform}\n\nSnake Blog Converted: ${res.data.snakeBlogTitle}`);
        window.location.reload();
      }
    } catch (err) {
      setRevShareError(err.response?.data?.error || err.message);
    } finally {
      setRevShareRunning(false);
    }
  };

  // Sorting states
  const [sortColumn, setSortColumn] = useState('totalPayout');
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc' or 'desc'

  const [blogSortColumn, setBlogSortColumn] = useState('totalRevenue');
  const [blogSortDirection, setBlogSortDirection] = useState('desc');

  const [snakeSortColumn, setSnakeSortColumn] = useState('revenue');
  const [snakeSortDirection, setSnakeSortDirection] = useState('desc');
  const [snakeAdminRevExpanded, setSnakeAdminRevExpanded] = useState(false);

  // Fetch offer stats data
  const fetchOfferStats = async (customFilters = null) => {
    setOfferStatsLoading(true);
    try {
      // Use custom filters if provided, otherwise use state filters
      const activeFilters = customFilters || filters;

      const params = new URLSearchParams({
        start_date: activeFilters.startDate,
        end_date: activeFilters.endDate
      });

      if (activeFilters.networkCompany) params.append('network_company', activeFilters.networkCompany);
      if (activeFilters.brandName) params.append('brand_name', activeFilters.brandName);
      if (activeFilters.offerId) params.append('offer_id', activeFilters.offerId);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8080/api'}/affiliate/admin/offer-stats?${params}`,
        { withCredentials: true }
      );

      if (response.data.success) {
        console.log('✅ Offer stats received:', response.data);
        setOfferStatsData(response.data.stats);
        setOfferStatsSummary(response.data.summary);
        setFilterOptions(response.data.filters);
      } else {
        console.error('❌ API returned success:false', response.data);
      }
    } catch (error) {
      console.error('❌ Error fetching offer stats:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
    } finally {
      setOfferStatsLoading(false);
    }
  };

  // Fetch data when filters change
  useEffect(() => {
    if (activeTab === 0) {
      fetchOfferStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, filters]);

  // Load categories for Blog Stats
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await api.get('/categories/select');
        setBlogCategories(response.data || []);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };
    loadCategories();
  }, []);

  // Load affiliates for admin
  useEffect(() => {
    const loadAffiliates = async () => {
      try {
        const response = await api.get('/affiliates');
        setAffiliates(response.data || []);
      } catch (error) {
        console.error('Error loading affiliates:', error);
      }
    };
    loadAffiliates();
  }, []);

  // Fetch Blog Stats
  useEffect(() => {
    const fetchBlogStats = async () => {
      if (activeTab !== 2 && activeTab !== 3) return; // Fetch for both Blog Stats and Snake Stats tabs
      if (!blogDateDates.startDate || !blogDateDates.endDate) return; // Wait for dates

      setBlogStatsLoading(true);
      setBlogStatsError('');
      try {
        const targetAffiliateId = !viewAllAffiliates && selectedAffiliateId
          ? selectedAffiliateId
          : 'all';

        const params = new URLSearchParams({
          startDate: new Date(blogDateDates.startDate + 'T00:00:00').toISOString(),
          endDate: new Date(blogDateDates.endDate + 'T23:59:59').toISOString()
        });

        if (blogSelectedCategory) {
          params.append('categoryId', blogSelectedCategory);
        }
        if (blogSelectedSubcategory) {
          params.append('subcategoryId', blogSelectedSubcategory);
        }
        if (viewAllAffiliates) {
          params.append('viewAll', 'true');
        }

        console.log('Fetching blog performance stats for affiliate:', targetAffiliateId);

        const response = await api.get(`/affiliate/blog-performance/${targetAffiliateId}?${params}`);

        console.log('Blog performance stats response:', response.data);
        setBlogStats(response.data?.stats || null);
      } catch (error) {
        console.error('Error fetching blog stats:', error);
        setBlogStatsError('Failed to load blog performance data');
        setBlogStats(null);
      } finally {
        setBlogStatsLoading(false);
      }
    };

    fetchBlogStats();
  }, [activeTab, blogDateDates, blogSelectedCategory, blogSelectedSubcategory, selectedAffiliateId, viewAllAffiliates]);

  // Toggle blog expansion
  const handleToggleBlog = (blogPostId) => {
    setExpandedBlogs(prev => ({
      ...prev,
      [blogPostId]: !prev[blogPostId]
    }));
  };

  // Toggle affiliate expansion
  const handleToggleAffiliate = (affiliateId) => {
    setExpandedAffiliates(prev => ({
      ...prev,
      [affiliateId]: !prev[affiliateId]
    }));
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };



  const handleClearFilters = () => {
    const clearedFilters = {
      ...filters,
      networkCompany: '',
      brandName: '',
      offerId: ''
    };
    setFilters(clearedFilters);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        setSortColumn('');
        setSortDirection('asc');
      }
    } else {
      setSortColumn(column);
      setSortDirection('desc'); // Default to desc for numeric columns
    }
  };

  const handleBlogSort = (column) => {
    if (blogSortColumn === column) {
      setBlogSortDirection(blogSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setBlogSortColumn(column);
      setBlogSortDirection('desc');
    }
  };

  const handleSnakeSort = (column) => {
    if (snakeSortColumn === column) {
      setSnakeSortDirection(snakeSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSnakeSortColumn(column);
      setSnakeSortDirection('desc');
    }
  };

  // Sort the offer stats data
  const getSortedData = () => {
    if (!sortColumn) return offerStatsData;

    return [...offerStatsData].sort((a, b) => {
      let aValue, bValue;

      switch (sortColumn) {
        case 'networkCompany':
          aValue = (a.networkCompany || '').toLowerCase();
          bValue = (b.networkCompany || '').toLowerCase();
          break;
        case 'brandName':
          aValue = (a.brandName || '').toLowerCase();
          bValue = (b.brandName || '').toLowerCase();
          break;
        case 'offerTitle':
          aValue = (a.offerTitle || '').toLowerCase();
          bValue = (b.offerTitle || '').toLowerCase();
          break;
        case 'clicks':
          aValue = a.clicks || 0;
          bValue = b.clicks || 0;
          break;
        case 'conversions':
          aValue = a.conversions || 0;
          bValue = b.conversions || 0;
          break;
        case 'cpa':
          aValue = a.cpa || 0;
          bValue = b.cpa || 0;
          break;
        case 'totalPayout':
          aValue = a.totalPayout || 0;
          bValue = b.totalPayout || 0;
          break;
        default:
          return 0;
      }

      const comparison = typeof aValue === 'string'
        ? aValue.localeCompare(bValue)
        : aValue - bValue;

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const renderSortIcon = (column, currentSortColumn, currentSortDirection) => {
    // For backward compatibility with simpler calls
    const targetColumn = currentSortColumn !== undefined ? currentSortColumn : sortColumn;
    const targetDirection = currentSortDirection !== undefined ? currentSortDirection : sortDirection;

    if (targetColumn !== column) {
      return <UnfoldMoreIcon sx={{ fontSize: 16, ml: 0.5, opacity: 0.3 }} />;
    }
    return targetDirection === 'asc' ? (
      <ArrowUpwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
    ) : (
      <ArrowDownwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
    );
  };


  const contextValue = {
    activeTab, setActiveTab,
    adsenseSuccess, setAdsenseSuccess,
    adsensePingResult, setAdsensePingResult,
    adsensePingLoading, setAdsensePingLoading,
    adsenseGlobalConnected, setAdsenseGlobalConnected,
    adsenseGlobalEmail, setAdsenseGlobalEmail,
    checkingAdsenseStatus, setCheckingAdsenseStatus,
    blogStats, setBlogStats,
    blogStatsLoading, setBlogStatsLoading,
    blogStatsError, setBlogStatsError,
    expandedBlogs, setExpandedBlogs,
    expandedAffiliates, setExpandedAffiliates,
    expandedSnakeBlogs, setExpandedSnakeBlogs,
    expandedCCRows, setExpandedCCRows,
    blogPreset, setBlogPreset,
    blogDateDates, setBlogDateDates,
    blogCategories, setBlogCategories,
    blogSelectedCategory, setBlogSelectedCategory,
    blogSelectedSubcategory, setBlogSelectedSubcategory,
    affiliates, setAffiliates,
    selectedAffiliateId, setSelectedAffiliateId,
    viewAllAffiliates, setViewAllAffiliates,
    offerStatsData, setOfferStatsData,
    offerStatsLoading, setOfferStatsLoading,
    offerStatsSummary, setOfferStatsSummary,
    filterOptions, setFilterOptions,
    offerPreset, setOfferPreset,
    filters, setFilters,
    revShareRunning, setRevShareRunning,
    revShareResult, setRevShareResult,
    revShareError, setRevShareError,
    revSharePayout, setRevSharePayout,
    revShareCleaning, setRevShareCleaning,
    runRevShareTest, cleanupRevShareTest, clearAllReportingData, runSimpleVerificationTest,
    sortColumn, setSortColumn,
    sortDirection, setSortDirection,
    blogSortColumn, setBlogSortColumn,
    blogSortDirection, setBlogSortDirection,
    snakeSortColumn, setSnakeSortColumn,
    snakeSortDirection, setSnakeSortDirection,
    snakeAdminRevExpanded, setSnakeAdminRevExpanded,
    handleToggleBlog, handleToggleAffiliate,
    handleFilterChange, handleClearFilters,
    handleSort, handleBlogSort, handleSnakeSort,
    getSortedData, renderSortIcon, fetchOfferStats,
    affiliateStatsData, categoryData,
  };

  return (
    <ReportingContext.Provider value={contextValue}>
      <DashboardLayout title="Reporting">
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="body1" color="text.secondary">
              Comprehensive analytics and reporting dashboard
            </Typography>
          </Box>

          <Paper sx={{ mb: 4 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Offer Stats" />
              <Tab label="Affiliate Stats" sx={{ display: 'none' }} />
              <Tab label="Blog Stats" />
              <Tab label="Snake Stats" />
              <Tab label="Category Stats" sx={{ display: 'none' }} />
              <Tab label="Platform Settings" sx={{ display: 'none' }} />
              <Tab
                id="revshare-test-tab"
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ScienceIcon sx={{ fontSize: 16 }} />
                    RevShare Test
                  </Box>
                }
                sx={{
                  display: showRevTestTab ? 'inline-flex' : 'none',
                  fontWeight: 600,
                  color: activeTab === 6 ? '#1a237e' : 'inherit',
                  '&.Mui-selected': { color: '#1a237e' }
                }}
              />
            </Tabs>
          </Paper>

          <Box sx={{ py: 3 }}>
            {activeTab === 0 && <OfferStatsTab />}
            {activeTab === 1 && <AffiliateStatsTab />}
            {activeTab === 2 && <BlogStatsTab />}
            {activeTab === 3 && <SnakeStatsTab />}
            {activeTab === 4 && <CategoryStatsTab />}
            {activeTab === 5 && <PlatformSettingsTab />}
            {activeTab === 6 && <RevShareTestTab />}
          </Box>
        </Container>
      </DashboardLayout>
    </ReportingContext.Provider>
  );
};

export default Reporting;
